import { cn } from "@/lib/cn";
import { useSelf, useUpdateMyPresence } from "@liveblocks/react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type SyntheticEvent,
} from "react";
import { CollaborationEnabledProvider } from "../providers/CollaborationEnabledProvider";

const MESSAGE_TTL_MS = 4000;
const FADE_OUT_MS = 1500;
const MAX_MESSAGE_LENGTH = 140;
const MESSAGE_PLACEHOLDER = "Say something";
const BUBBLE_FONT = "500 14px sans-serif";
const BUBBLE_HORIZONTAL_PADDING = 28;
const MIN_TYPED_BUBBLE_WIDTH = 52;
const MAX_BUBBLE_WIDTH = 200;

let measureCanvas: HTMLCanvasElement | null = null;

type CursorMessage = {
  id: string;
  text: string;
  createdAt: number;
  expiresAt: number;
};

type CursorPresence = {
  x: number;
  y: number;
  id: string;
  message?: CursorMessage;
};

type UserInfo = {
  color: string;
  theme: "light" | "dark";
};

const stopEditorEventPropagation = (
  event: SyntheticEvent<HTMLTextAreaElement>,
) => {
  event.stopPropagation();
};

export const CursorMessageComposer = ({ id }: { id: string }) => {
  return (
    <CollaborationEnabledProvider>
      <Main id={id} />
    </CollaborationEnabledProvider>
  );
};

const Main = ({ id }: { id: string }) => {
  const updateMyPresence = useUpdateMyPresence();
  const cursor = useSelf((me) => {
    const presenceCursor = me.presence.cursor as CursorPresence | null;

    return presenceCursor?.id === id ? presenceCursor : null;
  });
  const selfInfo = useSelf((me) => me.info as UserInfo);
  const [isComposing, setIsComposing] = useState(false);
  const [isFading, setIsFading] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const cursorRef = useRef(cursor);
  const messageIdRef = useRef("");

  useEffect(() => {
    cursorRef.current = cursor;
  }, [cursor]);

  const clearMessage = useCallback(() => {
    const currentCursor = cursorRef.current;

    setDraft("");
    setIsFading(false);
    setIsComposing(false);
    messageIdRef.current = createMessageId();

    if (!currentCursor) return;

    updateMyPresence({
      cursor: omitCursorMessage(currentCursor),
    });
  }, [updateMyPresence]);

  const resetMessage = useCallback(() => {
    const currentCursor = cursorRef.current;

    setDraft("");
    setIsFading(false);
    messageIdRef.current = createMessageId();

    if (!currentCursor) return;

    updateMyPresence({
      cursor: omitCursorMessage(currentCursor),
    });
  }, [updateMyPresence]);

  const updateMessage = useCallback(
    (text: string) => {
      const currentCursor = cursorRef.current;
      if (!currentCursor) return;

      if (!text.trim()) {
        updateMyPresence({
          cursor: omitCursorMessage(currentCursor),
        });
        return;
      }

      const now = Date.now();

      updateMyPresence({
        cursor: {
          ...currentCursor,
          message: {
            id: messageIdRef.current,
            text,
            createdAt: now,
            expiresAt: now + MESSAGE_TTL_MS,
          },
        },
      });
    },
    [updateMyPresence],
  );

  const resizeInput = useCallback(() => {
    const input = inputRef.current;
    if (!input) return;

    input.style.height = "auto";
    input.style.height = `${input.scrollHeight}px`;
  }, []);

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value.slice(0, MAX_MESSAGE_LENGTH);

    setIsFading(false);
    setDraft(value);
    updateMessage(value);
    resizeInput();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    event.stopPropagation();

    if (event.key === "Enter") {
      event.preventDefault();
      resetMessage();
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      clearMessage();
    }
  };

  const handleBlur = (event: SyntheticEvent<HTMLTextAreaElement>) => {
    event.stopPropagation();
    clearMessage();
  };

  useEffect(() => {
    if (!isComposing) return;

    const frame = window.requestAnimationFrame(() => {
      inputRef.current?.focus();
      resizeInput();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [isComposing, resizeInput]);

  useEffect(() => {
    if (!isComposing) return;

    const handleGlobalEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape" || document.activeElement === inputRef.current)
        return;

      event.preventDefault();
      clearMessage();
    };

    window.addEventListener("keydown", handleGlobalEscape, { capture: true });

    return () =>
      window.removeEventListener("keydown", handleGlobalEscape, {
        capture: true,
      });
  }, [clearMessage, isComposing]);

  useEffect(() => {
    resizeInput();
  }, [draft, resizeInput]);

  useEffect(() => {
    if (!isComposing || !draft.trim()) return;

    const fadeTimeout = window.setTimeout(
      () => setIsFading(true),
      MESSAGE_TTL_MS,
    );
    const clearTimeout = window.setTimeout(
      clearMessage,
      MESSAGE_TTL_MS + FADE_OUT_MS,
    );

    return () => {
      window.clearTimeout(fadeTimeout);
      window.clearTimeout(clearTimeout);
    };
  }, [clearMessage, draft, isComposing]);

  useEffect(() => {
    const handleSlashShortcut = (event: globalThis.KeyboardEvent) => {
      if (
        event.defaultPrevented ||
        event.key !== "/" ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        cursorRef.current?.id !== id ||
        isTypingTarget(event.target)
      ) {
        return;
      }

      event.preventDefault();
      setDraft("");
      setIsFading(false);
      setIsComposing(true);
      messageIdRef.current = createMessageId();
    };

    window.addEventListener("keydown", handleSlashShortcut, { capture: true });

    return () =>
      window.removeEventListener("keydown", handleSlashShortcut, {
        capture: true,
      });
  }, [id]);

  if (!isComposing || !cursor || cursor.id !== id) return null;

  return (
    <div
      className="tw-pointer-events-none tw-absolute tw-z-[1000] tw-transition-[left,top] tw-duration-[60ms] tw-ease-linear"
      style={{
        left: `${cursor.x}%`,
        top: `${cursor.y}%`,
      }}
    >
      <textarea
        ref={inputRef}
        aria-label="Cursor message"
        className={cn(
          "tw-pointer-events-auto -tw-mt-1 tw-ml-3 tw-box-border tw-block tw-min-h-7 tw-max-w-[200px] tw-resize-none tw-overflow-hidden tw-rounded-[14px] tw-border-0 tw-px-2.5 tw-py-1 tw-text-left tw-font-sans tw-text-sm tw-font-medium tw-leading-5 tw-text-white tw-shadow-md tw-outline-none tw-transition-opacity tw-duration-[1500ms] placeholder:tw-text-white/45",
          {
            "tw-text-blue-base placeholder:tw-text-blue-base/45":
              selfInfo?.theme === "light",
            "tw-opacity-0": isFading,
            "tw-opacity-100": !isFading,
          },
        )}
        maxLength={MAX_MESSAGE_LENGTH}
        onBlur={handleBlur}
        placeholder={MESSAGE_PLACEHOLDER}
        rows={1}
        style={{
          backgroundColor: selfInfo?.color || "#ff24b8",
          width: getBubbleWidth(draft),
        }}
        value={draft}
        onChange={handleChange}
        onBeforeInputCapture={stopEditorEventPropagation}
        onClickCapture={stopEditorEventPropagation}
        onKeyDownCapture={handleKeyDown}
        onKeyUpCapture={stopEditorEventPropagation}
        onMouseDownCapture={stopEditorEventPropagation}
        onPointerDownCapture={stopEditorEventPropagation}
        onPasteCapture={stopEditorEventPropagation}
      />
    </div>
  );
};

const omitCursorMessage = (cursor: CursorPresence): CursorPresence => {
  const { message: _message, ...cursorWithoutMessage } = cursor;
  return cursorWithoutMessage;
};

const createMessageId = () => {
  if (crypto.randomUUID) return crypto.randomUUID();

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const getBubbleWidth = (text: string) => {
  const value = text.trim() ? text : MESSAGE_PLACEHOLDER;
  const minWidth = text.trim()
    ? MIN_TYPED_BUBBLE_WIDTH
    : measureBubbleText(MESSAGE_PLACEHOLDER) + BUBBLE_HORIZONTAL_PADDING;
  const measuredWidth = measureBubbleText(value) + BUBBLE_HORIZONTAL_PADDING;

  return `${Math.min(
    MAX_BUBBLE_WIDTH,
    Math.max(Math.ceil(minWidth), Math.ceil(measuredWidth)),
  )}px`;
};

const measureBubbleText = (text: string) => {
  if (typeof document === "undefined") return text.length * 7;

  measureCanvas ??= document.createElement("canvas");

  const context = measureCanvas.getContext("2d");
  if (!context) return text.length * 7;

  context.font = BUBBLE_FONT;

  return context.measureText(text).width;
};

const isTypingTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false;

  return !!target.closest(
    [
      "input",
      "textarea",
      "select",
      "[contenteditable='true']",
      "[contenteditable='']",
      ".fr-element",
      ".fr-box",
      "[role='dialog']",
      "[data-radix-popper-content-wrapper]",
    ].join(","),
  );
};
